import * as React from 'react';
import { RankedBars } from '@/components/ui/charts';
import { RatingChip } from '@/components/ui/rating-chip';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import type { ReportBlock } from '@/lib/api/vendor-intelligence-report';
import { formatDate } from '@/lib/utils/format';
import { OUTCOME_LABEL } from '../../vi-logic';
import { DISPOSITION_LABEL, isShortlisted, productOf, type ReportModel } from '../report-model';
import { PageMissing, PageNote, PageTable, TD, TD_NUM, TH, TH_RIGHT } from './frame';

/**
 * The code tables and figures of the report pages (T-0283; E24-design §5;
 * report-contract v2), the React equivalents of what
 * `packages/pdf-render/src/vendor-report-v2.ts` prints from the same frozen
 * tables: the cover tiles, the needs table, the requirements by client
 * area, the ranking bars, the sensitivity checks across every vendor, the
 * set-aside table and the sources (the area matrix is the Shortlist step's
 * `AreaMatrix`, through `report-area-matrix.tsx`). Every number is the
 * server's: a token the report fills, or a table it composed. Nothing here
 * adds anything up. Where a table was not frozen (a draft prepared before
 * it), the page falls back to the frozen fit.
 */

// ---------------------------------------------------------------------------
// Cover
// ---------------------------------------------------------------------------

export function CoverTiles({
  tiles,
}: {
  tiles: Array<{ label: string; value: string; note?: string }>;
}) {
  return (
    <dl
      className="m-0 mt-auto grid grid-cols-1 gap-4 border-t border-ink-100 pt-4 sm:grid-cols-3"
      data-testid="report-cover-tiles"
    >
      {tiles.map((t) => (
        <div key={t.label} className="flex flex-col gap-0.5">
          <dt className="text-meta font-semibold uppercase tracking-wider text-ink-500">
            {t.label}
          </dt>
          <dd className="m-0 text-h2 font-semibold text-ink-900">{t.value}</dd>
          {t.note ? <dd className="m-0 text-meta text-ink-500">{t.note}</dd> : null}
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// What the client told us (E24-design §7)
// ---------------------------------------------------------------------------

export function NeedsTable({ model }: { model: ReportModel }) {
  const needs = model.tables?.needs ?? null;
  const rows = needs?.rows ?? [];
  if (rows.length === 0) {
    return (
      <PageMissing>No confirmed Discovery evidence is linked to requirements yet.</PageMissing>
    );
  }
  const total = needs?.pain_points ?? rows.length;
  return (
    <>
      <PageTable label="What the client told us">
        <thead>
          <tr className="border-b border-ink-100">
            <th scope="col" className={TH}>
              Need
            </th>
            <th scope="col" className={TH}>
              What we heard
            </th>
            <th scope="col" className={TH}>
              RTM
            </th>
            <th scope="col" className={TH}>
              Strongest evidence
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const more = r.more_evidence > 0 ? ` · +${r.more_evidence} more` : '';
            const evidence = r.evidence_by_vendor.filter((e) => e.items.length > 0);
            return (
              <tr key={r.pain_point_id} className="border-b border-ink-100 last:border-b-0">
                <th scope="row" className={`${TD} font-semibold`}>
                  {r.need}
                </th>
                <td className={TD}>
                  {r.heard?.text ?? '–'}
                  {r.heard?.display || more ? (
                    <span className="mt-0.5 block text-meta text-ink-500">
                      {r.heard?.display ?? ''}
                      {more}
                    </span>
                  ) : null}
                </td>
                <td className={`${TD} whitespace-nowrap font-mono tabular-nums text-ink-700`}>
                  {r.rtm.map((x) => x.display).join(', ') || '–'}
                </td>
                <td className={TD}>
                  {evidence.length === 0
                    ? '–'
                    : evidence.map((e, j) => (
                        <React.Fragment key={e.product_id}>
                          <span className="text-ink-900">
                            {productOf(model, e.product_id)?.name ?? 'Vendor'}
                          </span>{' '}
                          <span className="font-mono text-mono-sm text-ink-700">
                            {e.items.map((i) => i.code).join(', ')}
                          </span>
                          {j < evidence.length - 1 ? ' · ' : null}
                        </React.Fragment>
                      ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </PageTable>
      <PageNote>
        {total > rows.length
          ? `${rows.length} of ${total} confirmed needs shown, by severity.`
          : `${total} confirmed ${total === 1 ? 'need' : 'needs'}, by severity.`}
      </PageNote>
    </>
  );
}

// ---------------------------------------------------------------------------
// The requirements by client area
// ---------------------------------------------------------------------------

export function RequirementsByAreaTable({ model }: { model: ReportModel }) {
  const t = model.tables?.requirements_by_area ?? null;
  // The frozen table, or (a draft prepared before it) the roll-up's areas and the fit's totals.
  const rows = t
    ? t.rows
    : (model.fit.areas ?? []).map((a) => ({
        key: a.key,
        name: a.name,
        critical: a.counts.critical,
        desired: a.counts.desired,
        weight: a.weight,
        unmapped: a.unmapped,
      }));
  if (rows.length === 0) {
    return (
      <PageMissing>No client areas were frozen with this draft. Prepare it again.</PageMissing>
    );
  }
  const totals = t?.totals ?? {
    critical: model.fit.requirements?.critical,
    desired: model.fit.requirements?.desired,
    weight: model.fit.total_weight,
    unmapped: model.fit.not_assessed?.requirements ?? 0,
  };
  return (
    <PageTable label="Requirements by client area">
      <thead>
        <tr className="border-b border-ink-100">
          <th scope="col" className={TH}>
            Client area
          </th>
          <th scope="col" className={TH_RIGHT}>
            Critical
          </th>
          <th scope="col" className={TH_RIGHT}>
            Desired
          </th>
          <th scope="col" className={TH_RIGHT}>
            Weight
          </th>
          <th scope="col" className={TH_RIGHT}>
            Not mapped
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.key} className="border-b border-ink-100">
            <th scope="row" className={`${TD} font-normal`}>
              {a.name}
            </th>
            <td className={TD_NUM}>{a.critical}</td>
            <td className={TD_NUM}>{a.desired}</td>
            <td className={TD_NUM}>{a.weight}</td>
            <td className={TD_NUM}>{a.unmapped}</td>
          </tr>
        ))}
        <tr>
          <th scope="row" className={`${TD} font-semibold`}>
            Total
          </th>
          <td className={`${TD_NUM} font-semibold text-ink-900`}>{totals.critical ?? '–'}</td>
          <td className={`${TD_NUM} font-semibold text-ink-900`}>{totals.desired ?? '–'}</td>
          <td className={`${TD_NUM} font-semibold text-ink-900`}>{totals.weight}</td>
          <td className={`${TD_NUM} font-semibold text-ink-900`}>{totals.unmapped}</td>
        </tr>
      </tbody>
    </PageTable>
  );
}

// ---------------------------------------------------------------------------
// The ranking (E24-design §5, page 6)
// ---------------------------------------------------------------------------

export function RankingFigure({ model }: { model: ReportModel }) {
  const bars = model.tables?.ranking?.bars ?? [];
  if (bars.length > 0) {
    return (
      <RankedBars
        caption="Fit index by vendor"
        bars={bars.map((b) => ({
          key: b.product_id,
          rank: b.rank_label,
          label: b.name,
          value: b.index,
          emphasis: b.shortlisted,
        }))}
      />
    );
  }
  // A draft prepared before the ranking table: every product of the fit, the unranked marked.
  return (
    <RankedBars
      caption="Fit index by vendor"
      bars={model.fit.products.map((p) => ({
        key: p.product_id,
        rank: p.rank === null ? null : `${p.rank}${(p.tied_with ?? []).length > 0 ? '=' : ''}`,
        label: p.name,
        note: p.rank === null ? 'No confirmed rating yet' : null,
        value: p.rank === null ? null : p.index,
        emphasis: isShortlisted(model, p.product_id),
      }))}
    />
  );
}

const OUTCOME_TONE: Record<string, StatusChipTone> = {
  holds: 'healthy',
  tied: 'amber',
  changes: 'at-risk',
  not_applicable: 'neutral',
};

function outcomeWords(outcome: string): string {
  return outcome in OUTCOME_LABEL ? OUTCOME_LABEL[outcome as keyof typeof OUTCOME_LABEL] : outcome;
}

export function SensitivityByVendorTable({ model }: { model: ReportModel }) {
  const s = model.tables?.sensitivity_by_vendor ?? null;
  if (!s) {
    return (
      <PageMissing>No sensitivity check was frozen with this draft. Prepare it again.</PageMissing>
    );
  }
  return (
    <>
      <PageTable label="Sensitivity checks">
        <thead>
          <tr className="border-b border-ink-100 align-bottom">
            <th scope="col" className={TH}>
              Scoring check
            </th>
            {s.products.map((p) => (
              <th
                key={p.product_id}
                scope="col"
                className={`${TH_RIGHT} whitespace-nowrap text-ink-900`}
              >
                {p.name}
              </th>
            ))}
            <th scope="col" className={TH}>
              Top three
            </th>
            <th scope="col" className={TH}>
              Outcome
            </th>
          </tr>
        </thead>
        <tbody>
          {s.rows.map((row) => {
            const applicable = row.outcome !== 'not_applicable';
            const base = row.outcome === 'base';
            return (
              <tr key={row.key} className="border-b border-ink-100 align-top last:border-b-0">
                <th scope="row" className={`${TD} whitespace-nowrap font-normal`}>
                  {row.label}
                </th>
                {s.products.map((p, i) => {
                  const v = row.indexes[i];
                  return (
                    <td key={p.product_id} className={TD_NUM}>
                      {applicable && typeof v === 'number' ? v.toFixed(1) : '–'}
                    </td>
                  );
                })}
                <td className={TD}>
                  {applicable && row.top.length > 0 ? row.top.map((t) => t.name).join(', ') : '–'}
                </td>
                <td className="px-3 py-2">
                  {base ? (
                    <span className="text-body-sm font-semibold text-ink-900">Base</span>
                  ) : (
                    <>
                      <StatusChip tone={OUTCOME_TONE[row.outcome] ?? 'neutral'} size="sm">
                        {outcomeWords(row.outcome)}
                      </StatusChip>
                      {row.detail && row.outcome !== 'holds' && applicable ? (
                        <span className="mt-1 block text-meta text-ink-500">{row.detail}</span>
                      ) : null}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </PageTable>
      {s.summary ? <PageNote>{s.summary}</PageNote> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Considered and set aside (E24-design §5, page n-1)
// ---------------------------------------------------------------------------

const DISPOSITION_TONE: Record<string, StatusChipTone> = {
  shortlisted: 'healthy',
  alternate: 'amber',
  set_aside: 'neutral',
  not_assessed: 'unset',
};

export function SetAsideTable({ model }: { model: ReportModel }) {
  const t = model.tables?.set_aside ?? null;
  const rows = t?.rows ?? [];
  const groups = (t?.not_assessed ?? []).filter((g) => g.products.length > 0);
  if (rows.length === 0 && groups.length === 0) {
    return <PageMissing>Every vendor in scope is on the shortlist.</PageMissing>;
  }
  return (
    <PageTable label="Considered and set aside">
      <thead>
        <tr className="border-b border-ink-100">
          <th scope="col" className={TH}>
            Vendor
          </th>
          <th scope="col" className={TH_RIGHT}>
            Index
          </th>
          <th scope="col" className={TH}>
            Disposition
          </th>
          <th scope="col" className={TH}>
            Reason
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.product_id} className="border-b border-ink-100 align-top">
            <th scope="row" className={`${TD} font-semibold`}>
              {r.name ?? productOf(model, r.product_id)?.name ?? 'Vendor'}
            </th>
            <td className={TD_NUM}>{typeof r.index === 'number' ? r.index.toFixed(1) : '–'}</td>
            <td className="px-3 py-2">
              <StatusChip tone={DISPOSITION_TONE[r.disposition] ?? 'neutral'} size="sm">
                {DISPOSITION_LABEL[r.disposition] ?? r.disposition}
              </StatusChip>
            </td>
            <td className={`${TD} text-ink-700`}>{r.reason?.trim() ? r.reason : '–'}</td>
          </tr>
        ))}
        {groups.map((g, i) => (
          <tr key={`na-${i}`} className="border-b border-ink-100 align-top last:border-b-0">
            <th scope="row" className={`${TD} font-semibold`}>
              {g.products.map((p) => p.name).join(', ')}
            </th>
            <td className={TD_NUM}>–</td>
            <td className="px-3 py-2">
              <StatusChip tone="unset" size="sm">
                {DISPOSITION_LABEL.not_assessed}
              </StatusChip>
            </td>
            <td className={`${TD} text-ink-700`}>
              {g.platform_type ? `${g.platform_type}. ` : ''}No confirmed rating in the knowledge
              base, so no index.
            </td>
          </tr>
        ))}
      </tbody>
    </PageTable>
  );
}

// ---------------------------------------------------------------------------
// Method: the sources as read at draft time, when no template block says it
// ---------------------------------------------------------------------------

export function MethodSources({ model }: { model: ReportModel }) {
  const s = model.tables?.sources ?? null;
  const parts: string[] = [];
  const req = model.fit.requirements;
  if (req) {
    const areas = (model.fit.areas ?? []).length;
    parts.push(
      `Requirements: ${req.weighted} weighted ${req.weighted === 1 ? 'requirement' : 'requirements'} in ${areas} client ${areas === 1 ? 'area' : 'areas'}.`,
    );
  }
  if (s?.discovery) {
    const span = [s.discovery.from, s.discovery.to]
      .filter((d): d is string => Boolean(d))
      .map(formatDate)
      .join(' to ');
    parts.push(
      `Discovery: ${s.discovery.interviews} session ${s.discovery.interviews === 1 ? 'transcript' : 'transcripts'}${span ? `, ${span}` : ''}.`,
    );
  }
  const vendors = (s?.products ?? [])
    .map((v) => `${v.name} ${typeof v.items === 'number' ? v.items : '–'}`)
    .join(' · ');
  if (vendors) {
    parts.push(
      `Vendor evidence: confirmed items in the Caliber vendor knowledge base${s?.read_at ? ` as read on ${formatDate(s.read_at)}` : ''}: ${vendors}.`,
    );
  }
  if (parts.length === 0) return null;
  return <p className="m-0 text-body-sm text-ink-700">{parts.join(' ')}</p>;
}

/** A block's vendor, for the group heading the set-aside page draws over each Why paragraph. */
export function blockVendorName(model: ReportModel, block: ReportBlock): string {
  return (block.product_id && productOf(model, block.product_id)?.name) || 'Vendor';
}
